import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { UserService } from '../../services/user.service';
import { AlertsService } from '../../services/alerts.service';
import { SearchService } from '../../search/services/search.service';
import { MapService } from '../../map/services/map.service';
import { ConfirmComponent } from '../../components/confirm/confirm.component';
import { SaveLocationComponent } from '../save-location/save-location.component';
import { ImportLocationsComponent } from '../import-locations/import-locations.component';


/**
 * Component to manage a set of geographic locations.
 */
@Component({
	selector: 'saved-locations',
	templateUrl: './saved-locations.component.html',
	styleUrl: './saved-locations.component.css'
})
export class SavedLocationsComponent implements OnInit, OnDestroy {
	
	private userEventSubscription!: Subscription;

	LOCALIZATION: any;

	mylocations: any[] = [];

	constraints: any = {};
	pagination = {
		page: 1,
		elementsPerPage: 10,
		sorting: {
			sort: 'name,desc',
		},
		totalElements: 0,
		pageChanged: (newPage: any) => {
			this.pagination.page = newPage;
			this.loadSavedLocations(this.pagination.page);
		}
	};

	inprogress: boolean = false;

	constructor(
		private ngbModal: NgbModal,
		private userService: UserService,
		private alertsService: AlertsService,
		private mapService: MapService,
		private searchService: SearchService
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.loadSavedLocations();
		
		this.userEventSubscription = this.userService.broadcastEvent.subscribe((event: string) => {
			if ('localizationLoaded' === event) {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			}
		});
	}
	
	ngOnDestroy() {
		if (this.userEventSubscription) {
			this.userEventSubscription.unsubscribe();
		}
	}

	/**
	 * Loads the user's saved AOI locations using the current constraints.
	 */
	loadSavedLocations(page?: number): void {
		this.inprogress = true;

		if (page) {
			this.pagination.page = page;
		}

		let index = this.pagination.page - 1;

		this.mapService.getSavedLocations(index, this.pagination, this.constraints).then(
			(data: any) => {
				if (!data) {
					this.mylocations = [];
				} else {
					this.mylocations = data.content;
					this.pagination.page = data.page.number + 1;
					this.pagination.elementsPerPage = data.page.size;
					this.pagination.totalElements = data.page.totalElements;
				}
				this.inprogress = false;
			},
			(err: any) => {
				console.error("Failed to fetch user's saved AOI locations", err);
				this.mylocations = [];
				this.inprogress = false;
			}
		);
	}

	/**
	 * Imports AOI locations into the user's saved set.
	 */
	importLocations(): void {
		const modalRef = this.ngbModal.open(ImportLocationsComponent, {});

		modalRef.result.then(
			(payload) => {
				if (payload?.result.answer === 'ok') {
					this.loadSavedLocations(1);
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Executes a geo search using the geo properties of the given location.
	 */
	searchLocation(location: any, event?: Event): void {
		if (event) event.stopPropagation();
		this.searchService.broadcastCleanSearchesEvent();
		this.mapService.broadcastOpenMapEvent();
		this.mapService.searchByWKTLocation(location.geo);
	}

	/**
	 * Zooms to the given location on the system map.
	 */
	zoomToLocation(location: any, event?: Event): void {
		if (event) event.stopPropagation();
		this.mapService.broadcastOpenMapEvent();
		this.mapService.goToWKTLocation(location.geo);
	}

	/**
	 * Edits the given location properties.
	 */
	editLocation(location: any, event?: Event): void {
		if (event) event.stopPropagation();

		const modalRef = this.ngbModal.open(SaveLocationComponent, {});

		modalRef.componentInstance.locationData = {
			location: location,
			geo: null
		};

		modalRef.result.then(
			(payload) => {
				if (payload?.answer === 'ok') {
					if (payload.location.name) {
						this.loadSavedLocations();
					}
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Deletes the given location.
	 */
	deleteLocation(location: any, event?: Event): void {
		if (event) event.stopPropagation();

		const modalRef = this.ngbModal.open(ConfirmComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.modal = {
			callerName: 'deleteLocation',
			location: location,
			title: this.LOCALIZATION.deleteLocationHeader,
			message: this.LOCALIZATION.deleteLocationMessage,
			type: 'danger'
		};

		modalRef.result.then(
			(payload) => {
				if (payload.result?.answer === 'ok') {
					this.mapService.deleteLocation(location.id).then(
						(response: any) => {
							this.alertsService.success({
								message: this.LOCALIZATION.deleteLocationSuccess
							});
							this.loadSavedLocations(1);
							this.mapService.broadcastRedrawLocationsEvent();
						},
						(err: any) => {
							console.error("Failed to delete saved AOI location", err);
							this.alertsService.error({
								message: this.LOCALIZATION.deleteLocationError
							});
						}
					);
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}
}
